import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Modal, Table, Typography } from 'antd'
import fileDownload from 'js-file-download'
import { startCase, without } from 'lodash-es'
import moment from 'moment'

import { Button, Panel } from '../../../../core/components/styled'

import { LondonDateTime } from '../../../../common/components'
import {
  DEFAULT_PAGE,
  MENU_PROBING_JOB_STATUSES,
  SCRAPING_STATUSES,
} from '../../../../common/constants'
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../common/helpers'

import { MENU_STATUSES } from '../../../../menus/constants/menu-statuses'
import { generateAdvancedProbeSessionReport } from '../../../../reports/rest/advanced-probe-session'
import { generateMenuChangeFrequencyReport } from '../../../../reports/rest/menu-change-frequency'
import {
  ABORT_PROBING_SESSION,
  APPROVE_MENUS_FROM_ADVANCED_PROBING_SESSION,
  START_PROBING_SESSION,
} from '../../../graphql/mutations'
import {
  ADVANCED_PROBE_SESSION_MENUS,
  ADVANCED_PROBE_SESSIONS,
} from '../../../graphql/queries'

const SESSIONS_TABLE_PAGE_SIZE = 5
const SESSION_DETAILS_TABLE_PAGE_SIZE = 10

export const PROBING_SESSION_TYPES = {
  REGULAR: 'REGULAR',
  ADVANCED_PROBE_HTML_TEMPLATE: 'ADVANCED_PROBE_HTML_TEMPLATE',
  ADVANCED_PROBE_HTML_NON_TEMPLATE: 'ADVANCED_PROBE_HTML_NON_TEMPLATE',
}

const formatMenuStatus = status => {
  if (!status) return ''
  const label = MENU_STATUSES[status]?.name || status.toLowerCase()
  return startCase(label)
}
const formatProbingJobStatus = status => {
  if (!status) return ''
  const label = MENU_PROBING_JOB_STATUSES[status]?.name || status
  return startCase(label.toLowerCase())
}

const formatScrapingStatus = status => status && SCRAPING_STATUSES[status]?.name

const formatProbingStatus = status => status && SCRAPING_STATUSES[status]?.name

const buildSessionMenusColumns = isTemplate => {
  const columns = [
    {
      title: 'Menu ID',
      dataIndex: 'menuId',
      width: '5%',
      render: menuId => <Link to={`/menus/${menuId}`}>{menuId}</Link>,
    },
    {
      title: 'Menu Status (Current)',
      dataIndex: 'menuStatus',
      width: '10%',
      render: formatMenuStatus,
    },
    {
      title: 'Probing job status',
      dataIndex: 'probingJobStatus',
      width: '8%',
      render: formatProbingJobStatus,
    },
    {
      title: 'Probing status',
      dataIndex: 'probingStatus',
      width: '8%',
      render: formatProbingStatus,
    },
    {
      title: 'Probing schedule date',
      dataIndex: 'probingJobScheduleDate',
      width: '10%',
      render: date => <LondonDateTime date={date} />,
    },
    {
      title: 'Probing completed date',
      dataIndex: 'probingJobCompletedDate',
      width: '10%',
      render: date => <LondonDateTime date={date} />,
    },
    {
      title: 'Probing detected changes',
      dataIndex: 'probingDetectedChanges',
      width: '8%',
      render: value => {
        if (value === null || value === undefined) return ''
        return value ? 'Yes' : 'No'
      },
    },
    {
      title: 'Scraping job status',
      dataIndex: 'scrapingJobStatus',
      width: '10%',
      render: formatProbingJobStatus,
    },
    {
      title: 'Scraping status',
      dataIndex: 'scrapingStatus',
      width: '10%',
      render: formatScrapingStatus,
    },
    {
      title: 'Scraping detected changes',
      dataIndex: 'scrapingDetectedChanges',
      width: '8%',
      render: value => {
        if (value === null || value === undefined) return ''
        return value ? 'Yes' : 'No'
      },
    },
  ]

  if (isTemplate) {
    columns.splice(2, 0, {
      title: 'Cluster ID',
      dataIndex: 'clusterId',
      width: '5%',
    })
  }

  return columns
}

const createProbingSessionTab = ({ isTemplate }) => {
  const ProbingSessionTab = () => {
    const [sessionsPage, setSessionsPage] = useState(DEFAULT_PAGE)
    const [sessionMenusPage, setSessionMenusPage] = useState(DEFAULT_PAGE)
    const [selectedSessionId, setSelectedSessionId] = useState()
    const [downloadingSessionId, setDownloadingSessionId] = useState()
    const [
      downloadingMenuChangeDetections,
      setDownloadingMenuChangeDetections,
    ] = useState(false)
    const [abortSessionIds, setAbortSessionIds] = useState([])
    const [approvingSessionIds, setApprovingSessionIds] = useState([])

    const {
      data: {
        advancedProbeSessions: {
          advancedProbeSessions: sessionItems = [],
          total: sessionsTotal = 0,
        } = {},
      } = {},
      loading: isLoadingSessions,
      refetch: refetchSessions,
    } = useQuery(ADVANCED_PROBE_SESSIONS, {
      variables: {
        page: sessionsPage,
        pageSize: SESSIONS_TABLE_PAGE_SIZE,
        isTemplate,
      },
      fetchPolicy: 'no-cache',
    })

    useEffect(() => {
      setSessionMenusPage(DEFAULT_PAGE)
    }, [selectedSessionId])

    const {
      data: {
        advancedProbeSessionMenus: {
          advancedProbeSessionMenus: sessionMenus = [],
          total: sessionMenusTotal = 0,
        } = {},
      } = {},
      loading: isLoadingSessionMenus,
      refetch: refetchSessionMenus,
    } = useQuery(ADVANCED_PROBE_SESSION_MENUS, {
      variables: {
        sessionId: selectedSessionId,
        page: sessionMenusPage,
        pageSize: SESSION_DETAILS_TABLE_PAGE_SIZE,
      },
      skip: !selectedSessionId,
      fetchPolicy: 'no-cache',
    })

    const [startSession, { loading: isStartingSession }] = useMutation(
      START_PROBING_SESSION,
      {
        onCompleted({ startProbingSession: { id } = {} }) {
          showSuccessNotification({
            message: 'Advanced probe session started.',
            description: `Advanced probe session ${id} was started successfully.`,
          })
          setSelectedSessionId(id)
          setSessionsPage(DEFAULT_PAGE)
          refetchSessions()
        },
        onError({ message }) {
          showErrorNotification({
            message: 'Start advanced probe session failed.',
            description: message,
          })
        },
      },
    )

    const [abortSessionMutation] = useMutation(ABORT_PROBING_SESSION)

    const [approveMenusMutation] = useMutation(
      APPROVE_MENUS_FROM_ADVANCED_PROBING_SESSION,
    )

    const handleStartSession = () => {
      const type = isTemplate
        ? PROBING_SESSION_TYPES.ADVANCED_PROBE_HTML_TEMPLATE
        : PROBING_SESSION_TYPES.ADVANCED_PROBE_HTML_NON_TEMPLATE
      startSession({
        variables: { type },
      })
    }

    const handleDownloadReport = useCallback(async sessionId => {
      try {
        setDownloadingSessionId(sessionId)
        const { data } = await generateAdvancedProbeSessionReport({ sessionId })
        const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
        fileDownload(
          data,
          `advanced-probe-session-${sessionId}-${timestamp}.csv`,
        )
      } catch (error) {
        const { response, message } = error || {}
        showErrorNotification({
          message: 'Download advanced probe session report failed.',
          description: response?.statusText || message,
        })
      } finally {
        setDownloadingSessionId(undefined)
      }
    }, [])

    const handleDownloadMenuChangeDetections = useCallback(async () => {
      try {
        setDownloadingMenuChangeDetections(true)
        const menuType = isTemplate ? 'html-template' : 'html-non-template'
        const { data } = await generateMenuChangeFrequencyReport({ menuType })
        const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
        fileDownload(data, `menu-change-frequency-${menuType}-${timestamp}.csv`)
      } catch (error) {
        const { response, message } = error || {}
        showErrorNotification({
          message: 'Download menu change detections report failed.',
          description: response?.statusText || message,
        })
      } finally {
        setDownloadingMenuChangeDetections(false)
      }
    }, [])

    const handleAbortSession = useCallback(
      session => {
        const { id } = session

        Modal.confirm({
          title: 'Are you sure you want to abort this session?',
          content: `This will abort advanced probe session ${id}. This action cannot be undone.`,
          async onOk() {
            setAbortSessionIds(prevIds => [...prevIds, id])
            try {
              await abortSessionMutation({ variables: { id } })
              showSuccessNotification({
                message: 'Advanced probe session aborted.',
                description: `Advanced probe session ${id} was aborted successfully.`,
              })
              setSelectedSessionId(undefined)
              refetchSessions()
            } catch (error) {
              const { response, message } = error || {}
              showErrorNotification({
                message: 'Abort advanced probe session failed.',
                description: response?.statusText || message,
              })
            } finally {
              setAbortSessionIds(prevIds => without(prevIds, id))
            }
          },
        })
      },
      [abortSessionMutation, refetchSessions],
    )

    const handleApproveMenus = useCallback(
      session => {
        const { id } = session

        Modal.confirm({
          title: 'Are you sure you want to approve menus for this session?',
          content: `This will approve menus for advanced probe session ${id}.`,
          async onOk() {
            setApprovingSessionIds(prevIds => [...prevIds, id])
            try {
              const {
                data: {
                  approveMenusFromAdvancedProbingSession: {
                    approvedCount,
                    clustersProcessed,
                  } = {},
                } = {},
              } = await approveMenusMutation({ variables: { id } })
              showSuccessNotification({
                message: 'Menus approved successfully.',
                description: `Advanced probe session ${id}: ${approvedCount} menus approved across ${clustersProcessed} clusters.`,
              })
              if (selectedSessionId === id) {
                refetchSessionMenus({
                  sessionId: id,
                  page: sessionMenusPage,
                  pageSize: SESSION_DETAILS_TABLE_PAGE_SIZE,
                })
              }
            } catch (error) {
              const { message } = error || {}
              showErrorNotification({
                message: 'Approve menus failed.',
                description: message,
              })
            } finally {
              setApprovingSessionIds(prevIds => without(prevIds, id))
            }
          },
        })
      },
      [
        approveMenusMutation,
        refetchSessionMenus,
        selectedSessionId,
        sessionMenusPage,
      ],
    )

    const sessionColumns = useMemo(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          width: '5%',
        },
        {
          title: 'Created date',
          dataIndex: 'createdDate',
          width: '10%',
          render: date => <LondonDateTime date={date} />,
        },
        {
          title: 'Created by ID',
          dataIndex: 'createdById',
          width: '10%',
        },
        {
          title: 'Aborted date',
          dataIndex: 'abortedDate',
          width: '10%',
          render: date => <LondonDateTime date={date} />,
        },
        {
          title: 'Aborted by ID',
          dataIndex: 'abortedById',
          width: '10%',
        },
        {
          title: 'Total menus',
          dataIndex: 'totalMenuCount',
          width: '10%',
        },
        {
          title: 'Probed menus',
          dataIndex: 'probedMenuCount',
          width: '10%',
        },
        {
          title: 'Scraped menus',
          dataIndex: 'scrapedMenuCount',
          width: '10%',
        },
        {
          title: 'Actions',
          key: 'actions',
          width: '20%',
          render: session => (
            <>
              <Button
                icon="download"
                margin="no small no no"
                loading={downloadingSessionId === session.id}
                onClick={event => {
                  event.stopPropagation()
                  handleDownloadReport(session.id)
                }}
              >
                Session report
              </Button>
              <Button
                icon="stop"
                margin="no small no no"
                type="danger"
                disabled={
                  session.status !== 'IN_PROGRESS' ||
                  session.totalMenuCount === session.probedMenuCount
                }
                loading={abortSessionIds.includes(session.id)}
                onClick={event => {
                  event.stopPropagation()
                  handleAbortSession(session)
                }}
              >
                Abort
              </Button>
              {isTemplate && (
                <Button
                  icon="check"
                  margin="no small no no"
                  type="primary"
                  disabled={session.status !== 'IN_PROGRESS'}
                  loading={approvingSessionIds.includes(session.id)}
                  onClick={event => {
                    event.stopPropagation()
                    handleApproveMenus(session)
                  }}
                >
                  Approve
                </Button>
              )}
            </>
          ),
        },
      ],
      [
        abortSessionIds,
        approvingSessionIds,
        downloadingSessionId,
        handleAbortSession,
        handleApproveMenus,
        handleDownloadReport,
      ],
    )

    const sessionMenusColumns = buildSessionMenusColumns(isTemplate)

    return (
      <>
        <Panel margin="small no">
          <Button
            type="primary"
            icon="play-circle"
            loading={isStartingSession}
            onClick={handleStartSession}
            margin="no small no no"
          >
            Start new session
          </Button>
          <Button
            icon="download"
            loading={downloadingMenuChangeDetections}
            onClick={handleDownloadMenuChangeDetections}
          >
            Menu change frequency report
          </Button>
        </Panel>

        <Panel margin="small no">
          <Typography.Title level={4}>Sessions</Typography.Title>
          <Table
            rowKey="id"
            loading={isLoadingSessions}
            columns={sessionColumns}
            dataSource={sessionItems}
            pagination={{
              current: sessionsPage,
              total: sessionsTotal,
              pageSize: SESSIONS_TABLE_PAGE_SIZE,
              onChange: page => setSessionsPage(page),
            }}
            onRow={session => ({
              onClick: () => setSelectedSessionId(session.id),
              style:
                session.id === selectedSessionId
                  ? { backgroundColor: '#e6f7ff', cursor: 'pointer' }
                  : { cursor: 'pointer' },
            })}
          />
        </Panel>

        <Panel margin="small no">
          <Typography.Title level={4}>Session details</Typography.Title>
          <Table
            rowKey="menuId"
            loading={isLoadingSessionMenus}
            columns={sessionMenusColumns}
            dataSource={selectedSessionId ? sessionMenus : []}
            pagination={{
              current: sessionMenusPage,
              total: sessionMenusTotal,
              pageSize: SESSION_DETAILS_TABLE_PAGE_SIZE,
              onChange: page => setSessionMenusPage(page),
              showSizeChanger: false,
            }}
            locale={{
              emptyText: selectedSessionId
                ? 'No menus found for the selected session.'
                : 'Select a session to see its menus.',
            }}
          />
        </Panel>
      </>
    )
  }

  return ProbingSessionTab
}

export default createProbingSessionTab
